import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { FaComment, FaEdit, FaTrash, FaPlus, FaTimes, FaCheck } from 'react-icons/fa';
import './CommentsPanel.css';

const CommentsPanel = ({ rideId, currentTime, onSeek }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddComment, setShowAddComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const commentsListRef = useRef(null);
  const highlightedCommentRef = useRef(null);

  const user = auth.currentUser;

  // Time tolerance for highlighting (in seconds)
  const HIGHLIGHT_TOLERANCE = 2;

  // Load comments from Firebase
  useEffect(() => {
    if (!rideId) {
      console.log('CommentsPanel: No rideId provided');
      return;
    }

    console.log('CommentsPanel: Loading comments for ride:', rideId);
    setLoading(true);
    const commentsRef = collection(db, 'runs', rideId, 'comments');
    const q = query(commentsRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('CommentsPanel: Received snapshot with', snapshot.docs.length, 'comments');
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('CommentsPanel: Comments data:', commentsData);
      setComments(commentsData);
      setLoading(false);
    }, (error) => {
      console.error('CommentsPanel: Error loading comments:', error);
      console.error('CommentsPanel: Error code:', error.code);
      console.error('CommentsPanel: Error message:', error.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [rideId]);

  // Auto-scroll to highlighted comment
  useEffect(() => {
    if (highlightedCommentRef.current && commentsListRef.current) {
      const commentElement = highlightedCommentRef.current;
      const listElement = commentsListRef.current;
      
      // Calculate position to center the comment in the visible area
      const commentTop = commentElement.offsetTop;
      const commentHeight = commentElement.offsetHeight;
      const listHeight = listElement.clientHeight;
      
      const scrollPosition = commentTop - (listHeight / 2) + (commentHeight / 2);
      
      listElement.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentTime, comments]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddComment = async () => {
    if (!newCommentText.trim()) {
      console.log('CommentsPanel: No text entered');
      return;
    }
    
    if (!user) {
      console.log('CommentsPanel: No user logged in');
      alert('You must be logged in to add comments');
      return;
    }

    console.log('CommentsPanel: Adding comment for ride:', rideId);
    console.log('CommentsPanel: Comment text:', newCommentText.trim());
    console.log('CommentsPanel: Timestamp:', currentTime);
    console.log('CommentsPanel: User:', user.uid, user.email);

    try {
      const commentsRef = collection(db, 'runs', rideId, 'comments');
      const commentData = {
        text: newCommentText.trim(),
        timestamp: currentTime,
        author: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('CommentsPanel: Comment data to save:', commentData);
      const docRef = await addDoc(commentsRef, commentData);
      console.log('CommentsPanel: Comment saved successfully with ID:', docRef.id);

      setNewCommentText('');
      setShowAddComment(false);
    } catch (error) {
      console.error('CommentsPanel: Error adding comment:', error);
      console.error('CommentsPanel: Error code:', error.code);
      console.error('CommentsPanel: Error message:', error.message);
      alert('Failed to add comment: ' + error.message);
    }
  };

  const cancelAddComment = () => {
    setNewCommentText('');
    setShowAddComment(false);
  };

  const handleEditComment = async (commentId) => {
    if (!editingText.trim()) return;

    try {
      const commentRef = doc(db, 'runs', rideId, 'comments', commentId);
      await updateDoc(commentRef, {
        text: editingText.trim(),
        updatedAt: serverTimestamp()
      });

      setEditingCommentId(null);
      setEditingText('');
    } catch (error) {
      console.error('Error updating comment:', error);
      alert('Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const commentRef = doc(db, 'runs', rideId, 'comments', commentId);
      await deleteDoc(commentRef);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
    }
  };

  const startEditing = (comment) => {
    setEditingCommentId(comment.id);
    setEditingText(comment.text);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditingText('');
  };

  const toggleExpanded = (commentId) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const isCommentLong = (text) => {
    return text.length > 150;
  };

  const isCommentHighlighted = (commentTimestamp) => {
    return Math.abs(currentTime - commentTimestamp) <= HIGHLIGHT_TOLERANCE;
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      action();
    }
  };

  if (loading) {
    return (
      <div className="comments-panel">
        <div className="comments-header">
          <FaComment className="header-icon" />
          <h3>Comments</h3>
        </div>
        <div className="comments-loading">Loading comments...</div>
      </div>
    );
  }

  return (
    <div className="comments-panel">
      <div className="comments-header">
        <FaComment className="header-icon" />
        <h3>Comments</h3>
        <span className="comments-count">{comments.length}</span>
        {!showAddComment && (
          <button 
            className="add-comment-trigger-btn"
            onClick={() => setShowAddComment(true)}
            title="Add new comment"
          >
            <FaPlus />
          </button>
        )}
      </div>

      {/* Add New Comment Form */}
      {showAddComment && (
        <div className="add-comment-section">
          <div className="add-comment-header">
            <span className="timestamp-label">At {formatTime(currentTime)}</span>
          </div>
          <div className="add-comment-input-wrapper">
            <textarea
              className="comment-input"
              placeholder="Add a comment..."
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, handleAddComment)}
              rows={3}
              autoFocus
            />
            <div className="add-comment-actions">
              <button 
                className="add-comment-btn validate"
                onClick={handleAddComment}
                disabled={!newCommentText.trim()}
              >
                <FaCheck /> Validate
              </button>
              <button 
                className="add-comment-btn dismiss"
                onClick={cancelAddComment}
              >
                <FaTimes /> Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments List */}
      <div className="comments-list" ref={commentsListRef}>
        {comments.length === 0 ? (
          <div className="no-comments">
            <FaComment />
            <p>No comments yet</p>
            <p className="no-comments-hint">Add your first comment above</p>
          </div>
        ) : (
          comments.map((comment) => {
            const isHighlighted = isCommentHighlighted(comment.timestamp);
            const isExpanded = expandedComments.has(comment.id);
            const isLong = isCommentLong(comment.text);
            const isEditing = editingCommentId === comment.id;
            const isOwnComment = user && comment.author?.uid === user.uid;

            return (
              <div
                key={comment.id}
                ref={isHighlighted ? highlightedCommentRef : null}
                className={`comment-bubble ${isHighlighted ? 'highlighted' : ''}`}
              >
                {/* Comment Header */}
                <div className="comment-header">
                  <button 
                    className="comment-timestamp"
                    onClick={() => onSeek && onSeek(comment.timestamp)}
                    title="Jump to this moment"
                  >
                    {formatTime(comment.timestamp)}
                  </button>
                  <span className="comment-author">
                    {comment.author?.displayName || 'Unknown'}
                  </span>
                </div>

                {/* Comment Content */}
                <div className="comment-body">
                  {isEditing ? (
                    <div className="comment-edit-mode">
                      <textarea
                        className="comment-edit-input"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, () => handleEditComment(comment.id))}
                        autoFocus
                        rows={3}
                      />
                      <div className="comment-edit-actions">
                        <button 
                          className="edit-action-btn save"
                          onClick={() => handleEditComment(comment.id)}
                          disabled={!editingText.trim()}
                        >
                          <FaCheck /> Save
                        </button>
                        <button 
                          className="edit-action-btn cancel"
                          onClick={cancelEditing}
                        >
                          <FaTimes /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={`comment-text ${!isExpanded && isLong ? 'collapsed' : ''}`}>
                        {comment.text}
                      </p>
                      {isLong && (
                        <button 
                          className="expand-btn"
                          onClick={() => toggleExpanded(comment.id)}
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Comment Actions */}
                {isOwnComment && !isEditing && (
                  <div className="comment-actions">
                    <button 
                      className="comment-action-btn edit"
                      onClick={() => startEditing(comment)}
                      title="Edit comment"
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="comment-action-btn delete"
                      onClick={() => setShowDeleteConfirm(comment.id)}
                      title="Delete comment"
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}

                {/* Delete Confirmation */}
                {showDeleteConfirm === comment.id && (
                  <div className="delete-confirmation">
                    <p>Delete this comment?</p>
                    <div className="delete-actions">
                      <button 
                        className="delete-confirm-btn"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        Delete
                      </button>
                      <button 
                        className="delete-cancel-btn"
                        onClick={() => setShowDeleteConfirm(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CommentsPanel;

